import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { cardHref } from '@/lists/cellValue'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
  type StageEnd,
} from './record'
import { CopyValue, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import {
  fetchPackingList,
  fetchShipment,
  handOverToCarrier,
  markDelivered,
  postShipment,
  type PackingEntry,
  type Shipment,
} from './warehouseApi'
import {
  HistoryTab,
  OperationAlerts,
  ProductCell,
  StatusBadge,
  copyLinkItem,
  documentState,
  momentOr,
  newOperationKey,
  qty,
  short,
  stagesOf,
  sum,
  useOperation,
} from './warehouseRecord'
import '@/styles/record.css'

/** Этапы отгрузки в порядке прохождения: упаковка, проведение, передача, доставка. */
const STAGES = ['packed', 'shipped', 'in_transit', 'delivered'] as const

/**
 * Карточка отгрузки на дизайн-системе (Shipment.dc.html).
 *
 * **Проведение и передача перевозчику — разные действия**, и в шапке они разные кнопки
 * разных этапов: проведение — момент реализации (товар списан, выпущен УПД), передача —
 * момент, когда товар физически уехал. Слить их значит либо держать товар на балансе, пока
 * он лежит на доке, либо позволить задержке курьера сдвинуть дату реализации. Основное
 * действие одно и зависит от этапа: «Провести» у упакованной, «Передать перевозчику» у
 * проведённой, «Отметить доставку» у переданной.
 *
 * Проведение идёт `/wms/shipments/{id}/post` — той же операцией и под тем же правом, что
 * у терминала; ключ идемпотентности рождается при открытии подтверждения. Повторной заявки
 * перевозчику нет: кнопка исчезает с передачей, а сервер идемпотентен по ключу заявки.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Габариты, тип и брутто каждого места, SSCC и этикетки мест: упаковочный лист сервера
 *   отдаёт номер места, товар, количество и трек. Вес и объём — итогом отгрузки.
 * - Ячейка отбора, нетто и сумма строки, позиции счёта, не вошедшие в отгрузку.
 * - Водитель, машина, окно забора, договор с ТК: в отгрузке есть код перевозчика и трек.
 * - «Печать ТТН», «Печать упаковочного листа», «Отменить проведение», «Расформировать
 *   сборку», «Заполнить из счёта», «Сменить перевозчика»: таких операций API не публикует.
 * - Лента событий: единая лента (#79) складские документы не знает.
 */
export function ShipmentRecordView({ recordId, record, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState<string | null>(null)
  const [confirmPost, setConfirmPost] = useState(false)
  const [panel, setPanel] = useState<'handover' | 'deliver' | null>(null)
  const [operationKey, setOperationKey] = useState('')
  const [carrierCode, setCarrierCode] = useState('')
  const [deliveredAt, setDeliveredAt] = useState('')
  const operation = useOperation([
    ['shipment', recordId],
    ['shipment-packing', recordId],
    ['record', 'warehouse.shipment', recordId],
    ['list', 'warehouse.shipment'],
  ])

  const mayUpdate = can(user, 'warehouse.shipment', 'update')

  const shipmentQuery = useQuery({
    queryKey: ['shipment', recordId],
    queryFn: ({ signal }) => fetchShipment(recordId, signal),
    retry: false,
  })
  const packing = useQuery({
    queryKey: ['shipment-packing', recordId],
    queryFn: ({ signal }) => fetchPackingList(recordId, signal),
    retry: false,
  })

  const post = useMutation({
    mutationFn: () => postShipment(recordId, operationKey),
    onSuccess: () => {
      setConfirmPost(false)
      operation.succeed(t('record.wh.ship.posted'))
    },
    onError: (error) => {
      setConfirmPost(false)
      operation.fail(error)
    },
  })
  const handOver = useMutation({
    mutationFn: () => handOverToCarrier(recordId, carrierCode.trim()),
    onSuccess: () => {
      setPanel(null)
      setCarrierCode('')
      operation.succeed(t('card.wh.ship.handedOverDone'))
    },
    onError: operation.fail,
  })
  const deliver = useMutation({
    // An empty moment goes as null: the server then takes "now", and that is its decision,
    // not a date made up here.
    mutationFn: () => markDelivered(recordId, deliveredAt.trim() === '' ? null : deliveredAt),
    onSuccess: () => {
      setPanel(null)
      setDeliveredAt('')
      operation.succeed(t('card.wh.ship.deliveredDone'))
    },
    onError: operation.fail,
  })

  const gate = documentState(shipmentQuery, 'warehouse.shipment', '/shipments', t)
  if (gate) return gate
  const shipment = shipmentQuery.data as Shipment

  const entries = packing.data ?? []
  const places = groupPlaces(entries)
  const activeTab = tab ?? (shipment.packagesCount > 0 ? 'packing' : 'items')

  // ------------------------------------------------------------------ шапка

  const clientName = labelOf(record.values, 'counterparty_id')
  const clientHref = shipment.counterpartyId ? cardHref(user, 'sales.counterparty', shipment.counterpartyId) : null
  const orderLabel = labelOf(record.values, 'order_id')
  const warehouseLabel = labelOf(record.values, 'warehouse_id')

  const meta = [
    <StatusBadge key="status" entity="warehouse.shipment" status={shipment.status} />,
    shipment.counterpartyId ? (
      <span key="client" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {clientHref ? <Link to={clientHref}>{clientName ?? t('record.wh.ship.client')}</Link> : (clientName ?? t('record.wh.ship.client'))}
      </span>
    ) : null,
    orderLabel ? (
      <span key="order">
        {t('record.wh.ship.byOrder')} <span className="mono">{orderLabel}</span>
      </span>
    ) : null,
    warehouseLabel ? (
      <span key="warehouse" className="row" style={{ gap: 6 }}>
        <Icon name="box" size="sm" className="muted" />
        {t('record.wh.warehouseNamed', { name: warehouseLabel })}
      </span>
    ) : null,
    <CopyValue key="copy" value={shipment.number} label={t('record.copyNumber', { number: shipment.number })} />,
  ]

  const status = shipment.status
  const posted = shipment.postedAt != null
  const handedOver = shipment.handedOverAt != null
  let primary: HeaderAction | null = null
  if (mayUpdate && status === 'packed') {
    primary = {
      id: 'post',
      label: t('record.wh.ship.post'),
      icon: 'check',
      disabled: post.isPending,
      onClick: () => {
        operation.clear()
        setOperationKey(newOperationKey())
        setConfirmPost(true)
      },
    }
  } else if (mayUpdate && posted && !handedOver && status === 'shipped') {
    primary = { id: 'handover', label: t('card.wh.ship.handOverSubmit'), icon: 'truck', onClick: () => openPanel('handover') }
  } else if (mayUpdate && handedOver && shipment.deliveredAt == null && status === 'in_transit') {
    primary = { id: 'deliver', label: t('card.wh.ship.markDeliveredSubmit'), icon: 'check', onClick: () => openPanel('deliver') }
  }

  const menu: MenuAction[] = [copyLinkItem(t, operation.setFailure)]

  // The form lives next to the tab content; from the history tab it would open unseen.
  function openPanel(kind: 'handover' | 'deliver') {
    operation.clear()
    setPanel(kind)
    if (activeTab === 'history') setTab('items')
  }

  // ------------------------------------------------------------------ этапы и факты

  const reached = status === 'draft' || status === 'returned' ? (status === 'returned' ? 3 : 0) : STAGES.indexOf(status as (typeof STAGES)[number])
  const stageMeta = (code: string) => {
    switch (code) {
      case 'packed':
        return status === 'draft' ? t('record.wh.ship.stage.packing') : t('record.wh.ship.stage.packedMeta', { count: shipment.packagesCount })
      case 'shipped':
        // Three moments apart, each named when it has not happened: "not handed over" and
        // "no data" are different statements.
        return momentOr(shipment.postedAt, locale, t('card.wh.ship.notPosted'))
      case 'in_transit':
        return momentOr(shipment.handedOverAt, locale, t('card.wh.ship.notHandedOver'))
      default:
        return momentOr(shipment.deliveredAt, locale, t('card.wh.ship.notDelivered'))
    }
  }
  const ends: StageEnd[] = status === 'returned' ? [{ id: 'returned', label: t('record.wh.ship.returned'), tone: 'lost', active: true }] : []

  const gross = record.values.gross_weight_kg
  const net = record.values.net_weight_kg
  const volume = record.values.volume_m3
  const facts: Fact[] = [
    {
      id: 'positions',
      label: t('record.wh.positions'),
      value: qty(shipment.lines.length, locale),
      sub: t('record.wh.pieces', { qty: qty(sum(shipment.lines, (line) => line.quantity), locale) }),
    },
    {
      id: 'places',
      label: t('card.wh.ship.packages'),
      value: qty(shipment.packagesCount, locale),
      sub: shipment.packagesCount > 0 ? undefined : t('record.wh.ship.afterPacking'),
    },
    {
      id: 'gross',
      label: t('record.wh.ship.gross'),
      value: typeof gross === 'number' ? t('record.wh.kg', { qty: qty(gross, locale) }) : '—',
      sub: typeof net === 'number' ? t('record.wh.ship.netSub', { qty: qty(net, locale) }) : t('record.wh.ship.afterWeighing'),
    },
    {
      id: 'volume',
      label: t('record.wh.ship.volume'),
      value: typeof volume === 'number' ? t('record.wh.m3', { qty: qty(volume, locale) }) : '—',
      sub: typeof volume === 'number' ? undefined : t('record.wh.ship.afterPacking'),
    },
    {
      id: 'carrier',
      label: t('card.wh.ship.carrier'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{shipment.carrier ?? t('record.wh.ship.noCarrier')}</span>,
      // An empty tracking number is more honest than an invented one: a made-up one looks
      // valid, and boxes get searched by it.
      sub: shipment.trackingNumber ? (
        <span className="row" style={{ gap: 4 }}>
          {t('record.wh.ship.track')}{' '}
          <CopyValue value={shipment.trackingNumber} label={t('record.wh.ship.copyTrack', { number: shipment.trackingNumber })} />
        </span>
      ) : (
        <span className="muted">{handedOver ? t('record.wh.ship.noTrack') : t('record.wh.ship.trackAfterHandOver')}</span>
      ),
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const itemsPanel =
    shipment.lines.length === 0 ? (
      <StateView boxed kind="empty" title={t('card.wh.ship.noLines')} description={t('record.wh.ship.noLinesText')} />
    ) : (
      <Panel title={t('record.wh.ship.items')} count={shipment.lines.length} flush>
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.wh.ship.items')}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>№</th>
                <th>{t('card.wh.line.product')}</th>
                <th>{t('card.wh.line.batch')}</th>
                <th className="num">{t('card.wh.line.quantity')}</th>
                <th>{t('record.wh.ship.placesOf')}</th>
              </tr>
            </thead>
            <tbody>
              {shipment.lines.map((line, index) => (
                <tr key={line.id}>
                  <td className="muted">{index + 1}</td>
                  <td className="wrap">
                    <ProductCell id={line.productId} name={line.productName} sku={line.sku} />
                  </td>
                  <td>{line.batchId ? <span className="mono">{short(line.batchId)}</span> : <span className="muted">—</span>}</td>
                  <td className="num strong">{qty(line.quantity, locale)}</td>
                  <td>
                    {places
                      .filter((place) => place.items.some((item) => item.productId === line.productId))
                      .map((place) => place.no)
                      .join(', ') || '—'}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td />
                <td>{t('record.wh.total')}</td>
                <td />
                <td className="num">{qty(sum(shipment.lines, (line) => line.quantity), locale)}</td>
                <td>{shipment.packagesCount > 0 ? t('record.wh.ship.placesCount', { count: shipment.packagesCount }) : ''}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
    )

  const packingPanel = packing.isLoading ? (
    <p className="muted">{t('app.loading')}</p>
  ) : packing.isError ? (
    <StateView
      boxed
      kind={packing.error instanceof ApiError && packing.error.isForbidden ? 'forbidden' : 'error'}
      title={t('record.wh.ship.packingError')}
    />
  ) : places.length === 0 ? (
    // "Not packed" is a state of the shipment, not missing data: packing happens at the
    // bench, and an empty list means nobody got there yet.
    <StateView boxed kind="empty" title={t('card.wh.ship.notPacked')} />
  ) : (
    <Panel title={t('record.wh.ship.places')} count={places.length} flush>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" aria-label={t('record.wh.ship.places')}>
          <thead>
            <tr>
              <th>{t('card.wh.ship.packageNo')}</th>
              <th>{t('record.wh.ship.inside')}</th>
              <th>{t('card.wh.ship.tracking')}</th>
            </tr>
          </thead>
          <tbody>
            {places.map((place) => (
              <tr key={place.no}>
                <td>
                  <span className="strong">{`${place.no}/${places.length}`}</span>
                </td>
                <td className="wrap">
                  <span className="stack" style={{ gap: 2 }}>
                    {place.items.map((item, index) => (
                      <span key={`${item.productId}-${index}`} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                        <span>{item.productName ?? short(item.productId)}</span>
                        <span className="strong nowrap">{qty(item.quantity, locale)}</span>
                      </span>
                    ))}
                  </span>
                </td>
                {/* Tracking comes per place, not per shipment: some carriers number places,
                    others do not, and an empty cell is the carrier's protocol, not a gap. */}
                <td>{place.tracking ? <span className="mono">{place.tracking}</span> : <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel-f secondary">
        {typeof gross === 'number'
          ? t('record.wh.ship.weightNote', {
              gross: qty(gross, locale),
              net: typeof net === 'number' ? qty(net, locale) : '—',
            })
          : t('record.wh.ship.notWeighed')}
      </div>
    </Panel>
  )

  const documentHref = shipment.primaryDocumentId ? cardHref(user, 'billing.document', shipment.primaryDocumentId) : null
  const documentLabel = labelOf(record.values, 'primary_document_id')

  const sidebar = (
    <div className="rec-col">
      {panel === 'handover' && mayUpdate && (
        <Panel
          title={t('card.wh.ship.handOver')}
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPanel(null)}>
              {t('card.cancel')}
            </button>
          }
        >
          <div className="stack" style={{ gap: 8 }}>
            <label className="stack" style={{ gap: 4 }}>
              <span className="t-sm">{t('card.wh.ship.carrierCode')}</span>
              <input className="input" type="text" value={carrierCode} onChange={(event) => setCarrierCode(event.target.value)} />
            </label>
            <span className="t-sm secondary">{t('record.wh.ship.handOverHint')}</span>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                // The carrier code is required: a default would call a real courier from
                // the wrong company.
                disabled={carrierCode.trim() === '' || handOver.isPending}
                onClick={() => handOver.mutate()}
              >
                {t('card.wh.ship.handOverSubmit')}
              </button>
            </div>
          </div>
        </Panel>
      )}
      {panel === 'deliver' && mayUpdate && (
        <Panel
          title={t('card.wh.ship.markDelivered')}
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPanel(null)}>
              {t('card.cancel')}
            </button>
          }
        >
          <div className="stack" style={{ gap: 8 }}>
            <label className="stack" style={{ gap: 4 }}>
              <span className="t-sm">{t('card.wh.ship.deliveredAt')}</span>
              <input className="input" type="datetime-local" value={deliveredAt} onChange={(event) => setDeliveredAt(event.target.value)} />
            </label>
            <span className="t-sm secondary">{t('card.wh.ship.deliveredHint')}</span>
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={deliver.isPending} onClick={() => deliver.mutate()}>
                {t('card.wh.ship.markDeliveredSubmit')}
              </button>
            </div>
          </div>
        </Panel>
      )}
      <Panel title={t('card.wh.ship.carrier')}>
        <dl className="kv" style={{ margin: 0 }}>
          <dt>{t('card.wh.ship.carrier')}</dt>
          <dd>{shipment.carrier ?? '—'}</dd>
          <dt>{t('card.wh.ship.tracking')}</dt>
          <dd className="mono">{shipment.trackingNumber ?? '—'}</dd>
          <dt>{t('card.wh.ship.handedOver')}</dt>
          <dd>{momentOr(shipment.handedOverAt, locale, t('card.wh.ship.notHandedOver'))}</dd>
          <dt>{t('card.wh.ship.delivered')}</dt>
          <dd>{momentOr(shipment.deliveredAt, locale, t('card.wh.ship.notDelivered'))}</dd>
        </dl>
        {/* Why the hand-over is not offered, in words: unposted and already handed over are
            different reasons for the same absence. */}
        {!posted && <p className="t-sm secondary" style={{ margin: '8px 0 0' }}>{t('card.wh.ship.handOverNeedsPosting')}</p>}
        {handedOver && <p className="t-sm secondary" style={{ margin: '8px 0 0' }}>{t('card.wh.ship.alreadyHandedOver')}</p>}
      </Panel>
      <Panel title={t('record.wh.ship.documents')} flush>
        {orderLabel && (
          <div className="list-item">
            <Icon name="file" className="muted" />
            <span>
              {t('record.wh.ship.order')} <span className="mono">{orderLabel}</span>
            </span>
          </div>
        )}
        <div className="list-item">
          <Icon name="file" className="muted" />
          <div className="stack" style={{ flex: 1, gap: 0 }}>
            {shipment.primaryDocumentId ? (
              documentHref ? (
                <Link to={documentHref} className="mono">
                  {documentLabel ?? short(shipment.primaryDocumentId)}
                </Link>
              ) : (
                <span className="mono">{documentLabel ?? short(shipment.primaryDocumentId)}</span>
              )
            ) : (
              <span className="secondary">{t('record.wh.ship.noDocument')}</span>
            )}
            <span className="t-xs muted">
              {shipment.primaryDocumentId ? t('record.wh.ship.documentIssued') : t('record.wh.ship.documentOnPosting')}
            </span>
          </div>
        </div>
      </Panel>
      <Panel title={t('record.wh.details')}>{form}</Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.shipments'), to: listPath('warehouse.shipment', '/shipments') },
          { label: shipment.number, mono: true },
        ]}
        title={
          <>
            {t('record.wh.ship.title')} <span className="mono rec-mono-title">{shipment.number}</span>
          </>
        }
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OperationAlerts failure={operation.failure} notice={operation.notice} />
      {notices}
      {status === 'draft' && (
        <div className="alert alert-neutral" role="status">
          <Icon name="box" />
          <span>{t('record.wh.ship.packingOnTerminal')}</span>
        </div>
      )}
      {status === 'packed' && mayUpdate && (
        <div className="alert alert-neutral" role="status">
          <Icon name="alert" />
          <span>{t('record.wh.ship.postHint')}</span>
        </div>
      )}

      <StageBar
        ariaLabel={t('record.wh.stages')}
        steps={stagesOf(STAGES, reached, (code) => t(`record.wh.ship.stage.${code}`), stageMeta)}
        ends={ends}
      />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'items', label: t('card.tab.whShipmentLines'), count: shipment.lines.length },
          { id: 'packing', label: t('card.tab.whPacking'), count: places.length },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={activeTab}
        onChange={setTab}
        ariaLabel={t('record.wh.ship.sections')}
      >
        {activeTab === 'history' ? (
          <HistoryTab entity="warehouse.shipment" recordId={recordId} feedType="shipment" />
        ) : (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">{activeTab === 'items' ? itemsPanel : packingPanel}</div>
            {sidebar}
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirmPost}
        icon="alert"
        title={t('record.wh.ship.confirm.title', { number: shipment.number })}
        confirmLabel={t('record.wh.ship.post')}
        pending={post.isPending}
        onCancel={() => setConfirmPost(false)}
        onConfirm={() => post.mutate()}
      >
        <p>
          {t('record.wh.ship.confirm.text', {
            qty: qty(sum(shipment.lines, (line) => line.quantity), locale),
            positions: shipment.lines.length,
            warehouse: warehouseLabel ?? '—',
          })}
        </p>
        <p>{t('record.wh.ship.confirm.separate')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

interface Place {
  no: number
  tracking: string | null
  items: PackingEntry[]
}

/** Строки упаковочного листа по местам, в порядке номеров мест. */
function groupPlaces(entries: PackingEntry[]): Place[] {
  const byNo = new Map<number, Place>()
  for (const entry of entries) {
    const place = byNo.get(entry.packageNo) ?? { no: entry.packageNo, tracking: null, items: [] }
    place.items.push(entry)
    // A place's own number, never borrowed from a neighbour: a label with somebody else's
    // tracking number sends the box to somebody else's address.
    if (entry.trackingNumber) place.tracking = entry.trackingNumber
    byNo.set(entry.packageNo, place)
  }
  return [...byNo.values()].sort((a, b) => a.no - b.no)
}
